import { LinkIcon, XIcon } from "lucide-react";

import resolveSourcePreview from "../utils/resolveSourcePreview";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SmartColumnHeaderIcon } from "@/routes/smart-table/components/SmartTablePage/ColumnIcons";

type SourcePreviewRow = NonNullable<ReturnType<typeof resolveSourcePreview>>[number];

interface SourceSummaryRowsProps {
  summaries: SourcePreviewRow[];
  isReadonly: boolean;
  onOpenDialog: (resolver: string) => void;
  onRemoveSource: (index: number) => void;
}

const SourceSummaryRows = ({
  summaries,
  isReadonly,
  onOpenDialog,
  onRemoveSource,
}: SourceSummaryRowsProps) => (
  <div className="flex flex-col gap-2">
    {summaries.map((summary, index) => {
      let locationCaption: string | null = null;
      if (!summary.isStale && !summary.isResolving) {
        const tableLabel = summary.isCurrentTable ? "This table" : summary.tableTitle;
        locationCaption = `${summary.sheetTitle} · ${tableLabel}`;
      }

      let rowContainerClass = "bg-transparent hover:bg-gray-50/50";
      let ariaLabel = "Change source column";

      if (summary.isResolving) {
        rowContainerClass = "bg-gray-50/50";
        ariaLabel = "Resolving source column";
      } else if (summary.isStale) {
        rowContainerClass = summary.isPendingRemoval ? "bg-amber-50/60" : "bg-red-50/40";
        ariaLabel = summary.isPendingRemoval
          ? "Source column pending removal"
          : "Pick a different column";
      }

      const key = summary.resolver || String(index);
      const pointerEventsClass = isReadonly ? "pointer-events-none" : "";

      return (
        <div
          key={key}
          className={cn(
            "group/row relative flex items-center gap-2 rounded-md transition-colors",
            rowContainerClass,
            pointerEventsClass,
          )}
        >
          <button
            type="button"
            onClick={() => onOpenDialog(summary.resolver)}
            disabled={isReadonly}
            aria-label={ariaLabel}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 pl-2 pr-8 text-left text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "disabled:cursor-default",
            )}
          >
            {summary.isResolving && (
              <div className="flex flex-shrink-0 items-center justify-center text-gray-400">
                <LinkIcon className="h-4 w-4 animate-pulse" aria-hidden />
              </div>
            )}

            {!summary.isResolving && summary.isStale && (
              <div
                className={cn(
                  "flex flex-shrink-0 items-center justify-center",
                  summary.isPendingRemoval ? "text-amber-600" : "text-red-600",
                )}
              >
                <LinkIcon className="h-4 w-4" aria-hidden />
              </div>
            )}

            {!summary.isResolving && !summary.isStale && (
              <SmartColumnHeaderIcon
                columnType={summary.columnType}
                tone="default"
                className="h-5 w-5"
              />
            )}

            <div className="flex min-w-0 flex-1 items-center gap-2">
              {summary.isResolving && (
                <>
                  <span className="truncate font-medium text-gray-700">Resolving source…</span>
                  <span className="hidden min-w-0 max-w-[55%] truncate text-xs text-gray-400 sm:block">
                    Checking selected column availability.
                  </span>
                </>
              )}

              {!summary.isResolving && summary.isStale && (
                <>
                  <span
                    className={cn(
                      "truncate font-medium",
                      summary.isPendingRemoval ? "text-amber-700" : "text-red-700",
                    )}
                  >
                    {summary.isPendingRemoval
                      ? "This source will be removed after re-run"
                      : "This column isn't available anymore"}
                  </span>
                  <span
                    className={cn(
                      "hidden min-w-0 max-w-[55%] truncate text-xs sm:block",
                      summary.isPendingRemoval ? "text-amber-600/90" : "text-red-600/90",
                    )}
                  >
                    {summary.isPendingRemoval
                      ? "Re-run this composition column to apply."
                      : "Pick a different column to continue."}
                  </span>
                </>
              )}

              {!summary.isResolving && !summary.isStale && (
                <>
                  <OverflowDisplayTooltip content={summary.columnTitle} className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-700">
                      {summary.columnTitle}
                    </span>
                  </OverflowDisplayTooltip>
                  <OverflowDisplayTooltip
                    content={locationCaption}
                    className="hidden min-w-0 max-w-[55%] sm:block"
                  >
                    <span className="block truncate text-xs text-gray-400">{locationCaption}</span>
                  </OverflowDisplayTooltip>
                </>
              )}
            </div>
          </button>

          {!isReadonly && (
            <div
              className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 items-center gap-1",
                "hidden group-hover/row:flex",
              )}
            >
              {!summary.isResolving && summary.isStale && !summary.isPendingRemoval && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={event => {
                    event.stopPropagation();
                    onOpenDialog(summary.resolver);
                  }}
                  className="h-7 border-red-200 bg-white px-2.5 text-xs text-red-700 hover:bg-red-50 hover:text-red-800"
                >
                  Pick a different column
                </Button>
              )}

              {!summary.isResolving && !summary.isStale && (
                <BasicTooltip content="Clear">
                  <Button
                    type="button"
                    size="tinyIcon"
                    variant="ghost"
                    onClick={event => {
                      event.stopPropagation();
                      onRemoveSource(index);
                    }}
                    aria-label="Clear source"
                    className="h-6 w-6 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XIcon className="h-3 w-3" />
                  </Button>
                </BasicTooltip>
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export default SourceSummaryRows;
